/**
 * Memory tier for a Knowledge Archive concept — the one visual encoding the
 * tile wall uses (ROUTE_INVENTORY §2.10).
 *
 * The tier is derived from the node's FSRS fields the same way the backend
 * reasons about them: `stability` is the interval (in days) at which recall
 * probability has decayed to 90%, so retrievability is `0.9^(t/S)` with `t`
 * days since the last review.
 *
 * Two deliberate choices:
 *
 * - **Weak memory is amber, never red.** Progress here is guilt-free by
 *   product principle: a concept you are forgetting is an invitation to
 *   review, not an error state. The tiers therefore stop at `due` (strong
 *   warning tint) and nothing maps to `destructive`.
 * - **Studied but unreviewed is estimated, not frozen.** A concept the
 *   student has read but never reviewed decays from `estimated_stability`
 *   (see `retrievability`), so it can fade like any other. Only a concept
 *   with no study date at all, from before those were recorded, is left
 *   calm as `strong`: nothing to estimate from, and guessing "due" would
 *   nag about data nobody measured.
 */

import type { KnowledgeGraphNode } from '@/lib/api-client';

export type MemoryTier = 'new' | 'fresh' | 'strong' | 'fading' | 'due';

/** The fields the tier actually reads — tests build just these. */
export type MemoryTierInput = Pick<
  KnowledgeGraphNode,
  | 'stability'
  | 'last_reviewed_at'
  | 'created_at'
  | 'evidence_status'
  | 'review_count'
> &
  Partial<Pick<KnowledgeGraphNode, 'last_studied_at' | 'estimated_stability'>>;

/** `created_at` within this window (and not yet fading) reads as `fresh`. */
export const FRESH_WINDOW_DAYS = 7;

/** Below this retrievability a concept is overdue enough to surface. */
export const DUE_THRESHOLD = 0.75;

/** Between DUE_THRESHOLD and this, memory is visibly fading. */
export const FADING_THRESHOLD = 0.9;

const MS_PER_DAY = 86_400_000;

function daysSince(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, (now.getTime() - then) / MS_PER_DAY);
}

/**
 * True when recall is ESTIMATED rather than measured: the concept was studied
 * but never reviewed, so the backend stands in the stability a first review
 * would most likely have given (`estimated_stability`), decayed from the last
 * time it was studied.
 */
export function isEstimated(node: MemoryTierInput): boolean {
  const measured = node.stability != null && node.stability > 0;
  return (
    !measured &&
    node.estimated_stability != null &&
    node.estimated_stability > 0 &&
    !!node.last_studied_at
  );
}

/**
 * FSRS recall probability, or null when the node carries nothing to compute
 * it from.
 *
 * Measured when the concept has been reviewed (`stability`). Estimated when
 * it has only been studied (`isEstimated`): without that, a concept read a
 * year ago and never reviewed had no stability, could not decay, and stayed
 * "Learned" forever. The backend's daily plan decays the same estimate on
 * the same curve, so the map and the plan agree.
 */
export function retrievability(
  node: MemoryTierInput,
  now: Date = new Date()
): number | null {
  if (isEstimated(node)) {
    const t = daysSince(node.last_studied_at, now);
    if (t == null) return null;
    return Math.pow(0.9, t / (node.estimated_stability as number));
  }
  if (node.stability == null || node.stability <= 0) return null;
  const t = daysSince(node.last_reviewed_at ?? node.created_at, now);
  if (t == null) return null;
  return Math.pow(0.9, t / node.stability);
}

/** True when the concept carries no evidence of ever being studied. */
function isUnstudied(node: MemoryTierInput): boolean {
  if (node.evidence_status === 'not_started') return true;
  if (node.evidence_status === 'studied' || node.evidence_status === 'assessed')
    return false;
  // No evidence field (older mocks/fixtures): fall back to SR traces — any
  // stability, review count or review date is proof the concept was studied.
  return node.stability == null && !node.review_count && !node.last_reviewed_at;
}

export function memoryTier(
  node: MemoryTierInput,
  now: Date = new Date()
): MemoryTier {
  if (isUnstudied(node)) return 'new';

  const r = retrievability(node, now);
  if (r != null && r < DUE_THRESHOLD) return 'due';
  if (r != null && r < FADING_THRESHOLD) return 'fading';

  const age = daysSince(node.created_at, now);
  if (age != null && age <= FRESH_WINDOW_DAYS) return 'fresh';
  return 'strong';
}

/** Display order for legends and distribution bars: healthy → needs-you. */
export const MEMORY_TIER_ORDER: readonly MemoryTier[] = [
  'strong',
  'fresh',
  'fading',
  'due',
  'new',
];

export const MEMORY_TIER_LABEL: Record<MemoryTier, string> = {
  new: 'Not studied yet',
  fresh: 'Learned this week',
  strong: 'Strong memory',
  fading: 'Fading',
  due: 'Worth a review',
};

/**
 * Tier → mark, for the dot texture and the small indicator beside a name.
 *
 * ## One hue in a ramp, not five hues in a mosaic
 *
 * Memory strength is ORDINAL — solid, fading, gone, never learned — and the
 * first attempt encoded it with five saturated categorical hues (green, blue,
 * pale amber, amber, grey). Packed into a dense block that reads as a bug
 * chart, not a surface: the eye gets five competing signals and no order.
 * GitHub's contribution graph works precisely because it is one hue stepped
 * by intensity.
 *
 * So: ONE brand-hued ramp by opacity, plus amber as the single accent for the
 * one state that asks for action, plus a hollow square for absence. Four
 * marks, ordered, and the page now reads in Xonera's colour rather than in
 * generic status-green.
 *
 * **Opacity, not ramp steps.** `--brand-300` is lighter than `--brand-600` in
 * BOTH themes, so stepping the ramp would render "fading" brighter than
 * "strong" in dark mode and invert the meaning. Opacity degrades the same way
 * everywhere, and a faded memory being literally faded is the encoding
 * explaining itself.
 *
 * **`fresh` is not on the strength axis.** "Learned this week" is recency, not
 * strength, so it shares the solid mark and recency is carried by
 * `subjectStatusLine` in words. A fifth colour for it was the mosaic problem.
 */
export const TEXTURE_FILL: Record<MemoryTier, string> = {
  strong: 'fill-brand-600',
  fresh: 'fill-brand-600',
  fading: 'fill-brand-600/40',
  due: 'fill-warning',
  // Filled-but-faint, never a hollow outline. Outlined squares read as empty
  // checkboxes, break the block's surface and draw the eye to absence - the
  // one thing on this page that should recede.
  new: 'fill-muted-foreground/15',
};

export const MEMORY_TIER_DOT: Record<MemoryTier, string> = {
  strong: 'bg-brand-600',
  fresh: 'bg-brand-600',
  fading: 'bg-brand-600/40',
  due: 'bg-warning',
  new: 'bg-muted-foreground/20',
};

/**
 * The legend, which is the texture's four marks, not the five tiers. `fresh`
 * is deliberately absent: it draws as `strong`, and listing it would promise a
 * distinction the texture does not make.
 *
 * These four names are also what the concept panel calls a concept's state
 * (`legendLabel`), so the word under a dot on the map and the word in the
 * panel are always the same word.
 *
 * `strong` is "Learned", not "Solid": it also covers a concept you studied
 * but have never been tested on, and "Solid" claimed a strength nobody had
 * measured. "Learned" is true of both.
 */
export const TEXTURE_LEGEND: ReadonlyArray<{
  tier: MemoryTier;
  label: string;
}> = [
  { tier: 'strong', label: 'Learned' },
  { tier: 'fading', label: 'Fading' },
  { tier: 'due', label: 'Worth a review' },
  { tier: 'new', label: 'Not studied' },
];

/**
 * The recall range each state stands for, as the legend prints it, built
 * from the same thresholds `memoryTier` decides with so the key cannot quote
 * a number the map does not use. `new` has none: there is no memory to put a
 * figure on.
 */
export function recallRange(tier: MemoryTier): string | null {
  const fading = Math.round(FADING_THRESHOLD * 100);
  const due = Math.round(DUE_THRESHOLD * 100);
  switch (tier) {
    case 'strong':
    case 'fresh':
      return `${fading}%+`;
    case 'fading':
      return `${due}–${fading}%`;
    case 'due':
      return `<${due}%`;
    default:
      return null;
  }
}

/** A tier's name in the legend; `fresh` shares `strong`'s, as it does its colour. */
export function legendLabel(tier: MemoryTier): string {
  const shown = tier === 'fresh' ? 'strong' : tier;
  return TEXTURE_LEGEND.find(entry => entry.tier === shown)?.label ?? '';
}

/**
 * The one-line status a subject card prints under its texture — words, not a
 * bar. The old distribution bar was deleted rather than relocated: the texture
 * already IS the distribution, so a bar beside it said the same thing twice.
 *
 * Priority is what the student can act on, then what is new, then reassurance.
 */
export function subjectStatusLine(
  tierCounts: Record<MemoryTier, number>
): string {
  const parts: string[] = [];
  if (tierCounts.due > 0) parts.push(`${tierCounts.due} worth a review`);
  if (tierCounts.fading > 0) parts.push(`${tierCounts.fading} fading`);
  if (parts.length === 0 && tierCounts.fresh > 0)
    parts.push(`${tierCounts.fresh} new this week`);
  if (parts.length === 0 && tierCounts.new > 0)
    parts.push(`${tierCounts.new} not studied`);
  if (parts.length === 0) parts.push('All solid');
  return parts.join(' · ');
}
